import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { ArrowBack, Article, Edit, Notes, Save, Visibility } from '@mui/icons-material';
import {
	Alert,
	Box,
	Button,
	FormControlLabel,
	InputAdornment,
	ListItemIcon,
	ListItemText,
	MenuItem,
	Paper,
	Stack,
	Switch,
	Tab,
	Tabs,
	TextField,
	Typography,
} from '@mui/material';

import { useApiClient, useToastService } from '@@common';
import { CopyToClipboardButton, PasteFromClipboardButton, ViewTitleBar } from '@@components';

import { AuthoringResourceClient, AuthoringResourceModel, AuthoringResourceScopeType, SaveAuthoringResourceModel } from '../../api/api';
import { Permissions } from '../../common/Permissions';
import McpIcon from '../../common/components/McpIcon';
import StyledMarkdown from '../../common/components/StyledMarkdown';
import { useCurrentUserPermissions } from '../../components/CurrentUserPermissionsContext';
import ResourceTargetsEditor from './ResourceTargetsEditor';
import { resourceErrorMessage } from './resourceErrors';

const DEFAULT_MIME_TYPE = 'text/markdown';
const PLAIN_TEXT_MIME_TYPE = 'text/plain';
const MCP_INSTRUCTIONS_TAG = 'mcp:instructions';
const RESOURCE_TYPES = [
	{ icon: Article, label: 'Markdown', value: DEFAULT_MIME_TYPE },
	{ icon: Notes, label: 'Plain text', value: PLAIN_TEXT_MIME_TYPE },
];

type RouteParams = {
	id?: string;
};

type EditorState = SaveAuthoringResourceModel & { scopeType: AuthoringResourceScopeType; scopeId?: string };
type MarkdownTab = 'source' | 'preview';

const getResourceType = (mimeType: string) => RESOURCE_TYPES.find((resourceType) => resourceType.value === mimeType) ?? RESOURCE_TYPES[0];
const toSupportedMimeType = (mimeType?: string) => (RESOURCE_TYPES.some((resourceType) => resourceType.value === mimeType) ? mimeType! : DEFAULT_MIME_TYPE);

const normalizeEditorState = (resource: EditorState) => ({
	id: resource.id || undefined,
	name: resource.name,
	description: resource.description,
	mimeType: toSupportedMimeType(resource.mimeType),
	content: resource.content,
	scopeType: resource.scopeType,
	scopeId: resource.scopeId || undefined,
	targets: resource.targets,
	tags: resource.tags ?? [],
	isEnabled: resource.isEnabled,
	isMcpExposed: resource.isMcpExposed,
});

const hasResourceChanges = (current: EditorState, baseline: EditorState) =>
	JSON.stringify(normalizeEditorState(current)) !== JSON.stringify(normalizeEditorState(baseline));

const toTags = (value: string) =>
	value
		.split(',')
		.map((tag) => tag.trim())
		.filter(Boolean);

const hasTag = (tags: string[], targetTag: string) => tags.some((tag) => tag.toLowerCase() === targetTag.toLowerCase());

const setTagEnabled = (tags: string[], targetTag: string, isEnabled: boolean) => {
	const tagsWithoutTarget = tags.filter((tag) => tag.toLowerCase() !== targetTag.toLowerCase());

	return isEnabled ? [...tagsWithoutTarget, targetTag] : tagsWithoutTarget;
};

const fromResource = (resource: AuthoringResourceModel): EditorState => ({
	targets: resource.targets,
	id: resource.id,
	name: resource.name,
	description: resource.description,
	mimeType: toSupportedMimeType(resource.mimeType),
	content: resource.content,
	scopeType: resource.scopeType,
	scopeId: resource.scopeId,
	tags: resource.tags ?? [],
	isEnabled: resource.isEnabled,
	isMcpExposed: resource.isMcpExposed,
});

const toScopeType = (value: string | null) => {
	switch (value) {
		case AuthoringResourceScopeType.SubDomain:
			return AuthoringResourceScopeType.SubDomain;
		case AuthoringResourceScopeType.Context:
			return AuthoringResourceScopeType.Context;
		case AuthoringResourceScopeType.App:
			return AuthoringResourceScopeType.App;
		default:
			return AuthoringResourceScopeType.Tenant;
	}
};

const newResource = (searchParams: URLSearchParams): EditorState => {
	const scopeType = toScopeType(searchParams.get('scopeType'));

	return {
		name: '',
		description: '',
		mimeType: DEFAULT_MIME_TYPE,
		content: '',
		scopeType,
		targets: [{ type: scopeType, id: scopeType === AuthoringResourceScopeType.Tenant ? undefined : (searchParams.get('scopeId') ?? undefined) }],
		scopeId: scopeType === AuthoringResourceScopeType.Tenant ? undefined : (searchParams.get('scopeId') ?? undefined),
		tags: [],
		isEnabled: true,
		isMcpExposed: false,
	};
};

interface ResourceContentEditorProps {
	content: string;
	isMarkdown: boolean;
	onChange: (content: string) => void;
	onPasteFromClipboard: () => void;
}

function ResourceContentEditor({ content, isMarkdown, onChange, onPasteFromClipboard }: ResourceContentEditorProps) {
	const [markdownTab, setMarkdownTab] = useState<MarkdownTab>('preview');

	useEffect(() => {
		if (!isMarkdown && markdownTab === 'preview') {
			setMarkdownTab('source');
		}
	}, [isMarkdown, markdownTab]);

	return (
		<Box>
			<Tabs value={markdownTab} onChange={(_, value: MarkdownTab) => setMarkdownTab(value)} aria-label="Resource content editor mode" sx={{ minHeight: 40 }}>
				<Tab value="preview" icon={<Visibility fontSize="small" />} iconPosition="start" label="Preview" disabled={!isMarkdown} sx={{ minHeight: 40 }} />
				<Tab value="source" icon={<Edit fontSize="small" />} iconPosition="start" label="Source" sx={{ minHeight: 40 }} />
			</Tabs>
			{markdownTab === 'source' ? (
				<TextField
					label={isMarkdown ? 'Markdown' : 'Plain text'}
					value={content}
					onChange={(event) => onChange(event.target.value)}
					fullWidth
					multiline
					minRows={24}
					inputProps={{ spellCheck: false }}
					InputProps={{
						endAdornment: (
							<InputAdornment position="end" sx={{ alignSelf: 'flex-start', mt: 0.75 }}>
								<Stack direction="row" spacing={0.5}>
									<CopyToClipboardButton value={content} title="Copy source to clipboard" edge={false} disabled={!content} />
									<PasteFromClipboardButton onClick={onPasteFromClipboard} title="Paste source from clipboard" edge={false} />
								</Stack>
							</InputAdornment>
						),
					}}
					sx={{ mt: 1 }}
				/>
			) : (
				<Box
					sx={{
						mt: 1,
						p: 2,
						minHeight: 548,
						border: '1px solid',
						borderColor: 'divider',
						borderRadius: 1,
						bgcolor: 'background.default',
						overflow: 'auto',
					}}
				>
					{content.trim() ? (
						<StyledMarkdown>{content}</StyledMarkdown>
					) : (
						<Typography variant="body2" color="text.secondary">
							No markdown content yet.
						</Typography>
					)}
				</Box>
			)}
		</Box>
	);
}

export default function AuthoringResourceDetailView() {
	const { id } = useParams<RouteParams>();
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const client = useApiClient(AuthoringResourceClient);
	const toastService = useToastService();
	const queryClient = useQueryClient();
	const { hasPermission } = useCurrentUserPermissions();
	const isNew = id === 'new';
	const returnTo = searchParams.get('returnTo') || '/resources';
	const [editorValue, setEditorValue] = useState<EditorState>(() => newResource(searchParams));
	const [savedEditorValue, setSavedEditorValue] = useState<EditorState>(() => newResource(searchParams));
	const tagsValue = useMemo(() => editorValue.tags.join(', '), [editorValue.tags]);
	const isMcpInstructions = useMemo(() => hasTag(editorValue.tags, MCP_INSTRUCTIONS_TAG), [editorValue.tags]);
	const isMarkdown = editorValue.mimeType === DEFAULT_MIME_TYPE;
	const hasChanges = useMemo(() => hasResourceChanges(editorValue, savedEditorValue), [editorValue, savedEditorValue]);
	const editorValueRef = useRef(editorValue);
	const savedEditorValueRef = useRef(savedEditorValue);
	const loadedResourceIdRef = useRef<string | undefined>(undefined);

	const { data, isLoading, error } = useQuery({
		queryKey: ['authoringResource', id],
		queryFn: () => client.get(id!),
		enabled: !!id && !isNew,
	});

	useEffect(() => {
		editorValueRef.current = editorValue;
	}, [editorValue]);

	useEffect(() => {
		savedEditorValueRef.current = savedEditorValue;
	}, [savedEditorValue]);

	useEffect(() => {
		if (!data) {
			return;
		}

		const resource = fromResource(data);
		const isDifferentResource = loadedResourceIdRef.current !== resource.id;
		const currentHasChanges = hasResourceChanges(editorValueRef.current, savedEditorValueRef.current);
		loadedResourceIdRef.current = resource.id;

		setSavedEditorValue(resource);

		if (isDifferentResource || !currentHasChanges) {
			setEditorValue(resource);
		}
	}, [data]);

	const saveMutation = useMutation({
		mutationFn: (resource: EditorState) => client.save(resource),
		onSuccess: async (resource) => {
			const savedResource = fromResource(resource);
			toastService.success('Resource saved.');
			setEditorValue(savedResource);
			setSavedEditorValue(savedResource);
			await queryClient.invalidateQueries({ queryKey: ['authoringResources'] });
			await queryClient.invalidateQueries({ queryKey: ['authoringResource', resource.id] });

			if (isNew) {
				navigate(`/resources/${resource.id}?returnTo=${encodeURIComponent(returnTo)}`, { replace: true });
			}
		},
		onError: (error) => toastService.error(resourceErrorMessage(error, 'Failed to save resource.')),
	});

	const handlePasteSourceFromClipboard = async () => {
		try {
			const content = await navigator.clipboard.readText();
			setEditorValue((current) => ({ ...current, content }));
		} catch {
			toastService.error('Failed to paste from clipboard.');
		}
	};

	const handleBack = () => {
		if (hasChanges && !window.confirm('Discard unsaved resource changes?')) {
			return;
		}

		navigate(returnTo);
	};

	return (
		<Stack direction="column" spacing={0} sx={{ flexGrow: 1, minHeight: 0 }}>
			<Box sx={(theme) => ({ px: `${theme.layout.edgeGap}px`, pt: `${theme.layout.edgeGap}px` })}>
				<ViewTitleBar title={isNew ? 'Create Resource' : 'Edit Resource'}>
					<Button startIcon={<ArrowBack />} onClick={handleBack}>
						Back
					</Button>
					<Button
						variant="contained"
						startIcon={<Save />}
						onClick={() => saveMutation.mutate(editorValue)}
						disabled={
							saveMutation.isPending ||
							isLoading ||
							!!error ||
							(!isNew && !data) ||
							!hasChanges ||
							!editorValue.name.trim() ||
							[...(isNew ? [] : savedEditorValue.targets), ...editorValue.targets].some(
								(target) => !hasPermission(target.type === AuthoringResourceScopeType.App ? Permissions.AppUpdate : Permissions.ServiceUpdate),
							) ||
							editorValue.targets.some((target) => target.type !== AuthoringResourceScopeType.Tenant && !target.id)
						}
					>
						{saveMutation.isPending ? 'Saving...' : 'Save'}
					</Button>
				</ViewTitleBar>
			</Box>
			<Box sx={(theme) => ({ px: `${theme.layout.edgeGap}px`, pt: `${theme.layout.panelGap}px`, pb: `${theme.layout.edgeGap}px`, flexGrow: 1, minHeight: 0, display: 'flex' })}>
				<Paper variant="outlined" sx={{ p: 2, flexGrow: 1, minHeight: 0, overflow: 'auto' }}>
				<Stack spacing={2}>
					{error && <Alert severity="error">{resourceErrorMessage(error, 'Unable to load resource.')}</Alert>}
					<Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
						<TextField
							label="Name"
							value={editorValue.name}
							onChange={(event) => setEditorValue({ ...editorValue, name: event.target.value })}
							fullWidth
							autoFocus
						/>
						<TextField
							select
							label="Type"
							value={editorValue.mimeType}
							onChange={(event) => setEditorValue({ ...editorValue, mimeType: event.target.value })}
							SelectProps={{
								renderValue: (selected) => {
									const resourceType = getResourceType(String(selected));
									const Icon = resourceType.icon;

									return (
										<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
											<Icon fontSize="small" />
											{resourceType.label}
										</Box>
									);
								},
							}}
							sx={{ minWidth: 220 }}
						>
							{RESOURCE_TYPES.map((resourceType) => {
								const Icon = resourceType.icon;

								return (
									<MenuItem key={resourceType.value} value={resourceType.value}>
										<ListItemIcon>
											<Icon fontSize="small" />
										</ListItemIcon>
										<ListItemText>{resourceType.label}</ListItemText>
									</MenuItem>
								);
							})}
						</TextField>
						<FormControlLabel
							control={<Switch checked={editorValue.isEnabled} onChange={(event) => setEditorValue({ ...editorValue, isEnabled: event.target.checked })} />}
							label="Enabled"
							sx={{ alignSelf: { xs: 'flex-start', md: 'center' }, mr: 0, whiteSpace: 'nowrap' }}
						/>
					</Stack>
					<TextField
						label="Description"
						value={editorValue.description}
						onChange={(event) => setEditorValue({ ...editorValue, description: event.target.value })}
						fullWidth
						multiline
						minRows={2}
					/>
					<ResourceTargetsEditor value={editorValue.targets} onChange={(targets) => setEditorValue({ ...editorValue, targets })} />
					<Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
						<TextField label="Tags" value={tagsValue} onChange={(event) => setEditorValue({ ...editorValue, tags: toTags(event.target.value) })} fullWidth />
						<FormControlLabel
							control={
								<Switch checked={editorValue.isMcpExposed} onChange={(event) => setEditorValue({ ...editorValue, isMcpExposed: event.target.checked })} />
							}
							label={
								<Stack direction="row" spacing={0.75} alignItems="center">
									<McpIcon size={16} />
									<Typography component="span">MCP</Typography>
								</Stack>
							}
							sx={{ alignSelf: { xs: 'flex-start', md: 'center' }, mr: 0, whiteSpace: 'nowrap' }}
						/>
						<FormControlLabel
							control={
								<Switch
									checked={isMcpInstructions}
									onChange={(event) =>
										setEditorValue((current) => ({
											...current,
											tags: setTagEnabled(current.tags ?? [], MCP_INSTRUCTIONS_TAG, event.target.checked),
										}))
									}
								/>
							}
							label={
								<Stack direction="row" spacing={0.75} alignItems="center">
									<McpIcon size={16} />
									<Typography component="span">MCP Instruction</Typography>
								</Stack>
							}
							sx={{ alignSelf: { xs: 'flex-start', md: 'center' }, mr: 0, whiteSpace: 'nowrap' }}
						/>
					</Stack>
					<ResourceContentEditor
						content={editorValue.content}
						isMarkdown={isMarkdown}
						onChange={(content) => setEditorValue({ ...editorValue, content })}
						onPasteFromClipboard={() => void handlePasteSourceFromClipboard()}
					/>
					<Typography variant="caption" color="text.secondary">
						Enable MCP to make this document available to App assistants. Mark MCP Instruction for project conventions; other documents are reference material.
					</Typography>
				</Stack>
				</Paper>
			</Box>
		</Stack>
	);
}
